import { Button, Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material';
import { formatContentItemDate } from './formatContentItemDate';
import { type SavePublishState } from './ContentItemEditor.types';

// Trimmed from MUI's 9px so the two options sit closer to each other than to the copy above them.
// Not zero: it is what is left of the click target above and below the box.
const CHECKBOX_BLOCK_PADDING = 0.25;

type Props = {
    // 'Course' here. The button labels read 'Save Course' and 'Delete Course'. The heading belongs
    // to the card that holds the panel.
    itemHeader: string;
    title: string;
    modifiedAt: number | null | undefined;
    publishedAt: number | null | undefined;

    // Only an admin may publish, so only an admin sees the status line and the two checkboxes.
    canPublish: boolean;

    // True while a save or a delete is in flight. It disables both buttons and is the flag the
    // Angular panel called preventSubmit.
    busy: boolean;

    // True for one second after a save, which is how the Angular panel reported success: the Save
    // button read 'Saved' and then went back to its label.
    justSaved: boolean;

    publishState: SavePublishState;
    onPublishStateChange: (state: SavePublishState) => void;
    onSave: () => void;
    onDelete: () => void;
    saveDisabled: boolean;
};

// No published line at all when the course has never been published.
function PublishStatus({ title, modifiedAt, publishedAt }: Pick<Props, 'title' | 'modifiedAt' | 'publishedAt'>) {
    return (
        <Stack data-id="publish_status_message">
            <Typography variant="body1" dir="auto" sx={{ fontStyle: 'italic' }}>
                {title}
            </Typography>
            <Typography variant="body2">{`Last saved: ${formatContentItemDate(modifiedAt)}`}</Typography>
            {!!publishedAt && (
                <Typography variant="body2">{`Last published: ${formatContentItemDate(publishedAt)}`}</Typography>
            )}
        </Stack>
    );
}

/*
    The React version of the Angular content-item-save-publish directive, for the course editor. The
    Angular directive stays where it is for the playlist editor.

    Two blocks of the Angular template are gone rather than ported. The version history list read
    contentItem.version_history, which Lesson::Stream::ToJsonFromApiParams does not offer, so it has
    never rendered for a course. The Duplicate button was rendered only for a Playlist.
*/
export default function SavePublishPanel({
    itemHeader,
    title,
    modifiedAt,
    publishedAt,
    canPublish,
    busy,
    justSaved,
    publishState,
    onPublishStateChange,
    onSave,
    onDelete,
    saveDisabled,
}: Props) {
    return (
        <Stack spacing={3} data-id="publish_options">
            {canPublish && (
                <Stack spacing={2}>
                    <PublishStatus title={title} modifiedAt={modifiedAt} publishedAt={publishedAt} />

                    {/* A tight pair: they are alternatives, and the copy above belongs to both. */}
                    <Stack>
                        <FormControlLabel
                            data-id="publish_on_save"
                            control={
                                <Checkbox
                                    sx={{ paddingBlock: CHECKBOX_BLOCK_PADDING }}
                                    checked={publishState.shouldPublish}
                                    // The two are mutually exclusive, as they were in the Angular
                                    // watchers. Publishing and unpublishing in one save is not a
                                    // state the server can act on. Checkboxes rather than radios,
                                    // because this is the control admins already know here.
                                    onChange={event =>
                                        onPublishStateChange({
                                            shouldPublish: event.currentTarget.checked,
                                            shouldUnpublish: false,
                                        })
                                    }
                                />
                            }
                            label="Publish new version when I save."
                        />

                        {!!publishedAt && (
                            <FormControlLabel
                                data-id="unpublish_on_save"
                                control={
                                    <Checkbox
                                        sx={{ paddingBlock: CHECKBOX_BLOCK_PADDING }}
                                        checked={publishState.shouldUnpublish}
                                        onChange={event =>
                                            onPublishStateChange({
                                                shouldPublish: false,
                                                shouldUnpublish: event.currentTarget.checked,
                                            })
                                        }
                                    />
                                }
                                label="Unpublish when I save."
                            />
                        )}
                    </Stack>
                </Stack>
            )}

            <Divider />

            {/* Save first, with a rule and 24px before Delete. The confirm dialog is the real
                guard. */}
            <Stack direction="row" spacing={3} sx={{ alignItems: 'stretch' }}>
                <Button
                    type="button"
                    data-id="save"
                    variant="contained"
                    // Green, as the SEO save is: the two buttons that commit a record.
                    color="success"
                    onClick={onSave}
                    // justSaved too, or the second of two quick clicks saves again and writes
                    // another version. Angular held its guard across the whole flash.
                    disabled={busy || justSaved || saveDisabled}
                >
                    {justSaved ? 'Saved' : `Save ${itemHeader}`}
                </Button>

                <Divider orientation="vertical" flexItem />

                <Button
                    type="button"
                    data-id="delete"
                    variant="contained"
                    color="secondary"
                    onClick={onDelete}
                    disabled={busy}
                >
                    {`Delete ${itemHeader}`}
                </Button>
            </Stack>
        </Stack>
    );
}
